import React, { useState } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { WhatsAppStatusCard } from './WhatsAppStatusCard';
import { WhatsAppQuickStats } from './WhatsAppQuickStats';
import { WhatsAppMessageComposer } from './WhatsAppMessageComposer';
import { WhatsAppMessageHistory } from './WhatsAppMessageHistory';
import { WhatsAppTemplateList } from './WhatsAppTemplateList';
import { WhatsAppCampaigns } from './WhatsAppCampaigns';
import { WhatsAppAutomation } from './WhatsAppAutomation';
import { Activity, MessageSquare, FileText, Zap, BarChart3 } from 'lucide-react';

export const WhatsAppManagementDashboard: React.FC = () => {
  const [activeTab, setActiveTab] = useState('overview');

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">WhatsApp Management</h1>
        <p className="text-gray-600 mt-1">
          Manage WhatsApp integration, messages, templates, and campaigns
        </p>
      </div>

      {/* Quick Stats */}
      <WhatsAppQuickStats />

      {/* Main Content Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-3 md:grid-cols-5" aria-label="WhatsApp management sections">
          <TabsTrigger value="overview" className="flex items-center gap-2">
            <Activity className="h-4 w-4" />
            Overview
          </TabsTrigger>
          <TabsTrigger value="messages" className="flex items-center gap-2">
            <MessageSquare className="h-4 w-4" />
            Messages
          </TabsTrigger>
          <TabsTrigger value="templates" className="flex items-center gap-2">
            <FileText className="h-4 w-4" />
            Templates
          </TabsTrigger>
          <TabsTrigger value="campaigns" className="flex items-center gap-2">
            <Zap className="h-4 w-4" />
            Campaigns
          </TabsTrigger>
          <TabsTrigger value="automation" className="flex items-center gap-2">
            <BarChart3 className="h-4 w-4" />
            Automation
          </TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          <WhatsAppStatusCard />
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <WhatsAppMessageComposer />
            <div className="space-y-6">
              {/* Quick Actions Card */}
              <div className="bg-white rounded-lg border border-gray-200 p-6">
                <h3 className="text-lg font-semibold mb-4">Quick Actions</h3>
                <div className="space-y-2">
                  <button
                    onClick={() => setActiveTab('templates')}
                    className="w-full text-left px-4 py-3 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-medium">Create Template</span>
                      <FileText className="h-4 w-4 text-gray-400" />
                    </div>
                    <p className="text-sm text-gray-500 mt-1">
                      Create reusable message templates
                    </p>
                  </button>
                  <button
                    onClick={() => setActiveTab('campaigns')}
                    className="w-full text-left px-4 py-3 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-medium">Start Campaign</span>
                      <Zap className="h-4 w-4 text-gray-400" />
                    </div>
                    <p className="text-sm text-gray-500 mt-1">
                      Launch bulk messaging campaigns
                    </p>
                  </button>
                  <button
                    onClick={() => setActiveTab('automation')}
                    className="w-full text-left px-4 py-3 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-medium">Create Automation</span>
                      <BarChart3 className="h-4 w-4 text-gray-400" />
                    </div>
                    <p className="text-sm text-gray-500 mt-1">
                      Set up automated messaging rules
                    </p>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </TabsContent>

        {/* Messages Tab */}
        <TabsContent value="messages" className="space-y-6">
          <WhatsAppMessageHistory />
        </TabsContent>

        {/* Templates Tab */}
        <TabsContent value="templates" className="space-y-6">
          <WhatsAppTemplateList />
        </TabsContent>

        {/* Campaigns Tab */}
        <TabsContent value="campaigns" className="space-y-6">
          <WhatsAppCampaigns />
        </TabsContent>

        {/* Automation Tab */}
        <TabsContent value="automation" className="space-y-6">
          <WhatsAppAutomation />
        </TabsContent>
      </Tabs>
    </div>
  );
};
